<nz-spin [nzSpinning]="isLoading()">
  <div class="forecast-toolbar">
    <div class="forecast-toolbar__left">
      <nz-select
        style="width: 180px"
        [(ngModel)]="selectedPeriodMonths"
        [nzDisabled]="isGenerating"
        >
        @for (months of periodChoices; track months) {
          <nz-option [nzValue]="months" [nzLabel]="periodLabel(months)"></nz-option>
        }
      </nz-select>

      @if (options?.allowedModelStrategies?.length) {
        <div class="strategy-select">
          <nz-select
            style="width: 140px"
            [(ngModel)]="selectedStrategy"
            [nzDisabled]="isGenerating"
            >
            @for (strategy of options.allowedModelStrategies; track strategy) {
              <nz-option
                [nzValue]="strategy"
                [nzLabel]="strategy"
                [nzCustomContent]="true"
                >
                <span nz-tooltip [nzTooltipTitle]="strategyTooltip(strategy)" nzTooltipPlacement="bottomLeft">
                  {{ strategy }}
                </span>
              </nz-option>
            }
          </nz-select>
          <span
            nz-icon
            nzType="info-circle"
            class="strategy-info-icon"
            nz-tooltip
            [nzTooltipTitle]="strategyTooltip(selectedStrategy)"
            ></span>
        </div>
      }

      <nz-radio-group
        [ngModel]="groupVariants"
        (ngModelChange)="onGroupVariantsChange($event)"
        [nzDisabled]="isGenerating || !hasVariants"
        >
        <label nz-radio-button [nzValue]="false">Pro Variante</label>
        <label nz-radio-button [nzValue]="true">Gruppiert</label>
      </nz-radio-group>
    </div>

    <button
      nz-button
      nzType="primary"
      [nzLoading]="isGenerating"
      (click)="calculate()"
      >
      <span nz-icon nzType="reload"></span>
      Forecast berechnen
    </button>
  </div>

  @if (isGenerating && currentRun()?.result && hasViewableResult) {
    <nz-alert
      nzType="info"
      nzShowIcon
      nzMessage="Neuer Forecast wird berechnet – aktuelles Ergebnis bleibt sichtbar."
      style="margin-bottom: 12px"
      >
    </nz-alert>
  }

  @if (isGenerating && !currentRun()?.result) {
    <div class="forecast-generating">
      <nz-spin nzSimple></nz-spin>
      <div>Forecast wird berechnet…</div>
      <div class="muted">Das kann einige Sekunden dauern.</div>
    </div>
  } @else if (!currentRun()?.result && !isLoading()) {
    <nz-empty
      nzNotFoundContent="Noch kein Forecast berechnet"
      [nzNotFoundFooter]="emptyFooter"
      >
    </nz-empty>
    <ng-template #emptyFooter>
      <button nz-button nzType="primary" (click)="calculate()">Forecast berechnen</button>
    </ng-template>
  }

  @if (currentRun()?.result) {
    <div class="forecast-summary">
      <nz-statistic
        [nzValue]="totalStock | number:'1.0-0'"
        nzTitle="Bestand gesamt"
        >
      </nz-statistic>
      <nz-statistic
        [nzValue]="(currentRun()?.periodMonths || selectedPeriodMonths) + ' Monate'"
        nzTitle="Zeitraum"
        >
      </nz-statistic>
      @if (currentRun()?.modelStrategy) {
        <nz-statistic
          [nzValue]="currentRun()?.modelStrategy"
          nzTitle="Modell"
          >
        </nz-statistic>
      }
      @if (generatedAtLabel) {
        <div class="forecast-summary__item">
          <div class="forecast-summary__label">Berechnet am</div>
          <div class="forecast-summary__value">
            <span class="forecast-summary__metric">{{ generatedAtLabel }}</span>
          </div>
        </div>
      }
    </div>

    <div class="forecast-chart-controls">
      @if (!groupVariants && chartVariantChoices.length > 1) {
        <nz-select
          style="width: 280px"
          [(ngModel)]="selectedChartVariantId"
          (ngModelChange)="onChartVariantChange()"
          [nzDisabled]="isGenerating"
          >
          @for (variant of chartVariantChoices; track variant.articleVariantId) {
            <nz-option
              [nzValue]="variant.articleVariantId"
              [nzLabel]="variant.name || ('Variante ' + variant.articleVariantId)"
              >
            </nz-option>
          }
        </nz-select>
      }
      <span class="forecast-chart-hint">monatlich · Ist bis letzter voller Monat · Prognose ab aktuellem Monat · Bestand auf rechter Achse</span>
    </div>

    <div class="forecast-chart">
      @if (chartData.labels?.length) {
        <canvas
          baseChart
          [data]="chartData"
          [options]="chartOptions"
          [plugins]="[heutePlugin]"
          [type]="'line'"
          >
        </canvas>
      } @else if (isGenerating) {
        <div class="forecast-generating forecast-generating--inline">
          <nz-spin nzSimple></nz-spin>
          <div>Forecast wird berechnet…</div>
          <div class="muted">Das kann einige Sekunden dauern.</div>
        </div>
      } @else if (needsGroupedRecalculation) {
        <nz-empty
          nzNotFoundContent="Für die gruppierte Ansicht bitte erneut „Forecast berechnen“ ausführen."
          [nzNotFoundFooter]="groupedEmptyFooter"
          >
        </nz-empty>
        <ng-template #groupedEmptyFooter>
          <button
            nz-button
            nzType="primary"
            [nzLoading]="isGenerating"
            (click)="calculate()"
            >
            <span nz-icon nzType="reload"></span>
            Forecast berechnen
          </button>
        </ng-template>
      } @else {
        <nz-empty nzNotFoundContent="Keine Graphdaten verfügbar"></nz-empty>
      }
    </div>

    <nz-table
      #tableRef
      [nzData]="displayRows"
      [nzFrontPagination]="false"
      [nzShowPagination]="false"
      [nzLoading]="isGenerating"
      style="margin-top: 12px"
      >
      <thead>
        <tr>
          <th>Variante</th>
          <th>Ø Forecast / Monat</th>
          <th>Forecast Zeitraum</th>
          <th>Aktueller Bestand</th>
          <th>Verfügbar bis</th>
          <th>MHD-Status</th>
        </tr>
      </thead>
      <tbody>
        @for (row of tableRef.data; track row.articleVariantId ?? row.name) {
          <tr>
            <td>
              <div>{{ row.name || '—' }}</div>
              @if (row.orderNumber) {
                <div class="muted">{{ row.orderNumber }}</div>
              }
              @if (row.ean) {
                <div class="muted">EAN {{ row.ean }}</div>
              }
            </td>
            <td>
              @if (isInsufficientHistory(row)) {
                —
              } @else {
                {{ row.averageMonthlyForecast | number:'1.0-0' }}
              }
            </td>
            <td>
              @if (isInsufficientHistory(row)) {
                —
              } @else {
                {{ row.forecastTotal | number:'1.0-0' }}
              }
            </td>
            <td>{{ row.currentStock | number:'1.0-0' }}</td>
            <td>{{ formatAvailableUntil(row) }}</td>
            <td>
              <div class="mhd-cell">
                @if (row.mhd && !isInsufficientHistory(row)) {
                  <span class="mhd-cell__date">{{ formatMhdDate(row.mhd) }}</span>
                }
                @switch (mhdTagKind(row)) {
                  @case ('ok') {
                    <nz-tag nzColor="success">OK</nz-tag>
                  }
                  @case ('soon') {
                    <nz-tag nzColor="warning">MHD &lt; 3 Monate</nz-tag>
                  }
                  @case ('risk') {
                    <nz-tag nzColor="error">MHD-Risiko</nz-tag>
                  }
                  @default {
                    @if (!row.mhd) {
                      —
                    }
                  }
                }
                @if (isLowConfidence(row)) {
                  <nz-tag
                    nzColor="default"
                    nz-tooltip
                    [nzTooltipTitle]="row.message || 'Niedrige Modellkonfidenz'"
                    >
                    Low confidence
                  </nz-tag>
                }
                @if (row.seasonalGuardApplied) {
                  <nz-tag
                    nzColor="processing"
                    nz-tooltip
                    nzTooltipTitle="Das ARIMA-Modell lag deutlich unter dem Vorjahreswert. Verwendet werden die Werte des Vorjahresmonats."
                    >
                    Saisonschutz
                  </nz-tag>
                }
              </div>
            </td>
          </tr>
          @if (!isInsufficientHistory(row) && remainingStockLine(row)) {
            <tr class="remaining-stock-row">
              <td colspan="6">
                <span class="muted">Rest-Bestand / Monat: {{ remainingStockLine(row) }}</span>
              </td>
            </tr>
          }
        }
      </tbody>
    </nz-table>

    <div class="forecast-legend">
      <nz-tag nzColor="success">OK</nz-tag>
      <nz-tag nzColor="warning">MHD &lt; 3 Monate</nz-tag>
      <nz-tag nzColor="error">MHD-Risiko</nz-tag>
      <span class="muted">
        Die orange Linie zeigt den prognostizierten Bestandsverlauf bis 0;
        in der Tabelle als „Rest-Bestand / Monat“.
      </span>
    </div>
  }
</nz-spin>
